import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { IMinimalDevice } from '../../LoanerVehicles';

import classNames from 'classnames';

import { ReactComponent as SearchIcon } from '../../../../assets/svg/search.svg';
import { ReactComponent as ClearIcon } from '../../../../assets/svg/close-icon.svg';

import styles from './DeviceDropdown.module.scss';

interface IDeviceDropdownProps {
  devices: IMinimalDevice[];
  onSelect: (device: IMinimalDevice) => void;
  isOpen: boolean;
  onClose: () => void;
  placeholder?: string;
	triggerElement?: HTMLElement | null
}

const DeviceDropdown = ({
  devices,
  onSelect,
  isOpen,
  onClose,
  placeholder = "Search devices...",
	triggerElement
}: IDeviceDropdownProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredDevices, setFilteredDevices] = useState<IMinimalDevice[]>([])
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
	const [isVisible, setIsVisible] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

	const DROPDOWN_WIDTH = 280;
	const DROPDOWN_PADDING_TOP = 3;

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  const naturalSort = (a: IMinimalDevice, b: IMinimalDevice) => {
    const cleanedA = a.name?.trim().replace(/\s+/g, " ") || "";
    const cleanedB = b.name?.trim().replace(/\s+/g, " ") || "";

    return collator.compare(cleanedA, cleanedB);
  };

  // Calculate position based on trigger element
  useEffect(() => {
    if (isOpen && triggerElement) {
      const rect = triggerElement.getBoundingClientRect()
      setPosition({
        top: rect.bottom + window.scrollY + DROPDOWN_PADDING_TOP,
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
        width: rect.width,
      })

      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [isOpen, triggerElement])

  // Filter devices based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDevices([])
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = devices.filter((device) =>
        device.name.toLowerCase().includes(query)
      ).sort((a, b) => naturalSort(a, b));
      setFilteredDevices(filtered)
    }
  }, [searchQuery, devices])

  // Focus on search when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    } else {
      setSearchQuery('')
    }
  }, [isOpen])

  const handleDeviceSelect = (device: IMinimalDevice) => {
    onSelect(device)
    onClose()
    setSearchQuery('')
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const clearSearch = () => {
    setSearchQuery('')
    searchInputRef.current?.focus()
  }

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose()
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const dropdownContent = (
    <div
		  className={classNames(styles.dropdown, { [styles.visible]: isVisible })}
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${Math.max(position.width, DROPDOWN_WIDTH)}px`,
        zIndex: 100,
      }}
    >
      {/* Search */}
      <div className={styles.searchContainer}>
        <div className={styles.searchInputWrapper}>
          <SearchIcon className={styles.searchIcon} />

          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {/* Clear button */}
          {searchQuery && (
            <button
              className={styles.clearButton}
              onClick={clearSearch}
              title="Clear search"
            >
              <ClearIcon />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown header */}
      <div className={styles.dropdownHeader}>
        <h4>
          {searchQuery ? (
            <>
              {filteredDevices.length} device
              {filteredDevices.length !== 1 ? 's' : ''} found
              {searchQuery && ` for "${searchQuery}"`}
            </>
          ) : (
            `Select Device (${devices.length} available)`
          )}
        </h4>
      </div>

      {/* Dropdown items */}
      <div className={styles.dropdownList}>
        {filteredDevices.map((device) => (
          <div
            key={device.id}
            className={styles.dropdownItem}
            onClick={() => handleDeviceSelect(device)}
          >
            <div className={styles.deviceName}>{device.name}</div>
          </div>
        ))}
      </div>

      {/* No devices found message */}
      {searchQuery && filteredDevices.length === 0 && (
        <div className={styles.noDevices}>
          {searchQuery ? 'No devices found' : 'No devices available'}
        </div>
      )}
    </div>
  )

  return ReactDOM.createPortal(dropdownContent, document.body)
};

export default DeviceDropdown;